import { cn } from '@rental/ui'
import { Fragment, type ReactNode } from 'react'
import { ScrollRegion } from './ui'

export interface Column<Row> {
  header: string
  cell: (row: Row) => ReactNode
  className?: string
  /** Said, not shown: a column of buttons, each named by its row. */
  headerHidden?: boolean
  /** Amounts and counts: at the end of the column, in figures, on one line. */
  numeric?: boolean
  /** What names the row (a day, a point): read out before each of its cells. */
  rowHeader?: boolean
}

/** A row's own marks: a test id, and a row kept though replaced, struck through. */
export interface RowMarks {
  testId?: string
  superseded?: boolean
}

const CELL = 'px-3 py-2'
/**
 * A body row is a finger's height on a touch screen (UI-2g): one line of text
 * made a row 37.75 px tall, and a link's 44 px box reached into the next row's.
 */
const BODY_CELL = `${CELL} any-pointer-coarse:h-touch`

function align<Row>(column: Column<Row>) {
  return column.numeric ? 'figures text-end whitespace-nowrap' : 'text-start'
}

/**
 * A table of rows with one header row (DESIGN.md, "Glass"): rules between the
 * rows in `--color-rule`, headers in quiet text. It always sits in a region
 * the keyboard can scroll, so a wide table on a narrow screen stays readable.
 * Each header aligns itself: a `th` centres whenever its row keeps the initial
 * alignment, which `text-start` on the table is. Amounts end their column, in
 * figures, so their digits stand under each other (UI-2e).
 *
 * `after` puts what belongs to a row under it, across the table: a form opened
 * from the row, or why it was replaced. `foot` is the totals' row, named by its
 * header across the first columns.
 */
export function DataTable<Row>({
  label,
  caption,
  columns,
  rows,
  rowKey,
  testId,
  rowMarks,
  after,
  foot,
}: {
  /** The region's name, which a screen reader says when the focus enters it. */
  label: string
  /** The table's own name, said with its rows: needed when one region holds more than one table. */
  caption?: string
  columns: readonly Column<Row>[]
  rows: readonly Row[]
  rowKey: (row: Row, index: number) => string
  testId?: string
  rowMarks?: (row: Row) => RowMarks
  after?: (row: Row) => ReactNode
  foot?: { label: ReactNode; span: number; cells: readonly ReactNode[] }
}) {
  return (
    <ScrollRegion label={label}>
      <table className="w-full text-sm" data-testid={testId}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.header}
                scope="col"
                className={cn(
                  'border-b border-rule font-medium text-text-muted',
                  CELL,
                  column.numeric ? 'text-end' : 'text-start',
                )}
              >
                {column.headerHidden ? (
                  <span className="sr-only">{column.header}</span>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const marks = rowMarks?.(row) ?? {}
            const under = after?.(row) ?? null
            return (
              <Fragment key={rowKey(row, index)}>
                <tr
                  data-testid={marks.testId}
                  className={cn(
                    'align-top',
                    under === null ? 'border-b border-rule last:border-0' : '',
                    marks.superseded ? 'text-text-muted line-through' : '',
                  )}
                >
                  {columns.map((column) =>
                    column.rowHeader ? (
                      <th
                        key={column.header}
                        scope="row"
                        className={cn(BODY_CELL, 'font-medium', align(column), column.className)}
                      >
                        {column.cell(row)}
                      </th>
                    ) : (
                      <td
                        key={column.header}
                        className={cn(BODY_CELL, align(column), column.className)}
                      >
                        {column.cell(row)}
                      </td>
                    ),
                  )}
                </tr>
                {under === null ? null : (
                  <tr className="border-b border-rule last:border-0">
                    <td colSpan={columns.length} className={cn(CELL, 'pb-4')}>
                      {under}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
        {foot ? (
          <tfoot>
            <tr className="border-t border-rule font-bold">
              <th scope="row" colSpan={foot.span} className={cn(CELL, 'text-start')}>
                {foot.label}
              </th>
              {foot.cells.map((cell, index) => {
                const column = columns[foot.span + index]
                return (
                  // The columns never move: their position is their identity.
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed columns
                  <td key={index} className={cn(CELL, column ? align(column) : '')}>
                    {cell}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </ScrollRegion>
  )
}
